import { EmployeeView, PayrollEvidenceLineItemView, PayrollView } from '@/types/entity-types';
import { useEffect, useState } from 'react';

type OcrDemoProps = {
  currentPayroll: PayrollView;
  employees: EmployeeView[];
  onProcess: (mockOcrRecords: PayrollEvidenceLineItemView[]) => void;
};

export const OcrDemo = ({ currentPayroll, employees, onProcess }: OcrDemoProps) => {
  const [newRecord, setNewRecord] = useState<PayrollEvidenceLineItemView | null>(null);
  const [newRecords, setNewRecords] = useState<PayrollEvidenceLineItemView[]>([]);
  const [checkedRecords, setCheckedRecords] = useState<Set<string>>(new Set());
  const [editedNetPay, setEditedNetPay] = useState<Record<string, number>>({});
  const [evidenceFromCurrentPayroll, setEvidenceFromCurrentPayroll] = useState<PayrollEvidenceLineItemView[]>([]);
  const [evidenceFromEmployees, setEvidenceFromEmployees] = useState<PayrollEvidenceLineItemView[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    setEvidenceFromCurrentPayroll(
      currentPayroll.records.map((record) => ({
        id: generateEvidenceId(),
        firstName: record.employee.firstName,
        lastName: record.employee.lastName,
        netPay: record.netPay,
        employeeId: record.employeeId,
      })),
    );
  }, [currentPayroll]);

  useEffect(() => {
    setEvidenceFromEmployees(
      employees.map((record) => ({
        id: generateEvidenceId(),
        firstName: record.firstName,
        lastName: record.lastName,
        netPay: 0,
        employeeId: record.id,
        ...generateBankDetails(),
      })),
    );
  }, [employees]);

  useEffect(() => {
    generateNewRecord();
  }, []);

  const generateNewRecord = () => {
    setNewRecord({
      id: generateEvidenceId(),
      firstName: getRandomFirstName(),
      lastName: getRandomLastName(),
      netPay: getRandomNetPay(),
      employeeId: null,
      ...generateBankDetails(),
    });
  };

  const generateEvidenceId = () => {
    return `psli_${Math.random().toString(36).substring(2, 15)}`;
  };

  const generateBankDetails = () => {
    const ukBankDetails = {
      sortCode: Math.floor(Math.random() * 1000000)
        .toString()
        .padStart(6, '0'),
      accountNumber: Math.floor(Math.random() * 100000000)
        .toString()
        .padStart(8, '0'),
    };
    const irelandBankDetails = {
      iban:
        'IE29AIBK931152' +
        Math.floor(Math.random() * 100000000)
          .toString()
          .padStart(8, '0'),
    };
    return Math.random() < 0.5 ? ukBankDetails : irelandBankDetails;
  };

  const handleClickTest = () => {
    // Gather all records from different sources
    const allRecords = [...newRecords, ...evidenceFromCurrentPayroll, ...evidenceFromEmployees.slice(0, 10)];

    // Filter only checked records
    const checkedItems = allRecords.filter((record) => checkedRecords.has(record.id));

    // Apply edited net pay values
    const checkedItemsWithEdits = checkedItems.map((record) => ({
      ...record,
      netPay: editedNetPay[record.id] ?? record.netPay,
    }));

    setIsSubmitted(true);
    onProcess(checkedItemsWithEdits);
  };

  return (
    <div style={{ padding: '40px' }}>
      <table style={{ width: '100%' }}>
        <thead style={{ borderBottom: '5px solid #ddd' }}>
          <tr style={{ height: '30px' }}>
            <th style={{ width: '25%', textAlign: 'left' }}>First Name</th>
            <th style={{ width: '25%', textAlign: 'left' }}>Last Name</th>
            <th style={{ width: '15%', textAlign: 'left' }}>Net Pay</th>
            <th style={{ width: '20%', textAlign: 'left' }}>Note</th>
            <th style={{ width: '5%', textAlign: 'right' }}></th>
          </tr>
        </thead>
        <tbody>
          <tr style={{ height: '30px', visibility: isSubmitted ? 'hidden' : 'visible' }}>
            <td>{newRecord?.firstName}</td>
            <td>{newRecord?.lastName}</td>
            <td>{newRecord?.netPay}</td>
            <td>New</td>
            <td style={{ textAlign: 'right' }}>
              <input
                type="checkbox"
                disabled={isSubmitted}
                checked={false}
                onChange={() => {
                  if (newRecord) {
                    setNewRecords([newRecord, ...newRecords]);
                    setCheckedRecords(new Set([...checkedRecords, newRecord.id]));
                    generateNewRecord();
                  }
                }}
              />
            </td>
          </tr>
          {newRecords.map((record) => (
            <tr
              key={record.id}
              style={{
                height: '30px',
                fontWeight: isSubmitted ? (checkedRecords.has(record.id) ? 'bold' : 'normal') : 'normal',
              }}
            >
              <td>{record.firstName}</td>
              <td>{record.lastName}</td>
              <td>
                <input
                  type="number"
                  disabled={isSubmitted}
                  value={record.netPay}
                  onChange={(e) => {
                    setNewRecords(
                      newRecords.map((r) => (r.id === record.id ? { ...r, netPay: Number(e.target.value) } : r)),
                    );
                  }}
                  style={{ width: '80%', border: '1px solid #ccc' }}
                />
              </td>
              <td>New</td>
              <td style={{ textAlign: 'right' }}>
                <input
                  type="checkbox"
                  disabled={isSubmitted}
                  checked={checkedRecords.has(record.id)}
                  onChange={() => {
                    const newChecked = new Set(checkedRecords);
                    if (newChecked.has(record.id)) {
                      newChecked.delete(record.id);
                    } else {
                      newChecked.add(record.id);
                    }
                    setCheckedRecords(newChecked);
                  }}
                />
              </td>
            </tr>
          ))}
          {evidenceFromCurrentPayroll.map((record) => {
            return (
              <tr
                key={record.id}
                style={{
                  height: '30px',
                  fontWeight: isSubmitted ? (checkedRecords.has(record.id) ? 'bold' : 'normal') : 'normal',
                }}
              >
                <td>{record.firstName}</td>
                <td>{record.lastName}</td>
                <td>
                  <input
                    type="number"
                    disabled={isSubmitted}
                    value={editedNetPay[record.id] ?? record.netPay}
                    onChange={(e) => {
                      setEditedNetPay({
                        ...editedNetPay,
                        [record.id]: Number(e.target.value),
                      });
                    }}
                    style={{ width: '80%', border: '1px solid #ccc' }}
                  />
                </td>
                <td>Payroll</td>
                <td style={{ textAlign: 'right' }}>
                  <input
                    type="checkbox"
                    disabled={isSubmitted}
                    checked={checkedRecords.has(record.id)}
                    onChange={() => {
                      const newChecked = new Set(checkedRecords);
                      if (newChecked.has(record.id)) {
                        newChecked.delete(record.id);
                      } else {
                        newChecked.add(record.id);
                      }
                      setCheckedRecords(newChecked);
                    }}
                  />
                </td>
              </tr>
            );
          })}
          {evidenceFromEmployees.map((record) => (
            <tr
              key={record.id}
              style={{
                height: '30px',
                fontWeight: isSubmitted ? (checkedRecords.has(record.id) ? 'bold' : 'normal') : 'normal',
              }}
            >
              <td>{record.firstName}</td>
              <td>{record.lastName}</td>
              <td>
                <input
                  type="number"
                  disabled={isSubmitted}
                  value={editedNetPay[record.id] ?? record.netPay}
                  onChange={(e) => {
                    setEditedNetPay({
                      ...editedNetPay,
                      [record.id]: Number(e.target.value),
                    });
                  }}
                  style={{ width: '80%', border: '1px solid #ccc' }}
                />
              </td>
              <td>Employee</td>
              <td style={{ textAlign: 'right' }}>
                <input
                  type="checkbox"
                  disabled={isSubmitted}
                  checked={checkedRecords.has(record.id)}
                  onChange={() => {
                    const newChecked = new Set(checkedRecords);
                    if (newChecked.has(record.id)) {
                      newChecked.delete(record.id);
                    } else {
                      newChecked.add(record.id);
                    }
                    setCheckedRecords(newChecked);
                  }}
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!isSubmitted && (
        <div
          style={{
            borderTop: '5px solid #ddd',
            marginTop: '5px',
            textAlign: 'right',
          }}
        >
          <button
            style={{
              marginTop: '15px',
              border: '1px solid #000',
              padding: '5px 10px',
              borderRadius: '5px',
              backgroundColor: '#000',
              color: '#fff',
              cursor: 'pointer',
            }}
            onClick={handleClickTest}
          >
            TEST
          </button>
        </div>
      )}
    </div>
  );
};

const getRandomFirstName = () => {
  // prettier-ignore
  const names = [
    'James', 'Mary', 'John', 'Patricia', 'Robert', 'Jennifer', 'Michael', 'Linda',
    'William', 'Barbara', 'David', 'Elizabeth', 'Richard', 'Susan', 'Joseph', 'Jessica',
    'Thomas', 'Sarah', 'Charles', 'Karen', 'Christopher', 'Nancy', 'Daniel', 'Lisa',
    'Matthew', 'Betty', 'Anthony', 'Margaret', 'Mark', 'Sandra', 'Donald', 'Ashley',
    'Steven', 'Kimberly', 'Paul', 'Emily', 'Andrew', 'Donna', 'Joshua', 'Michelle',
    'Kenneth', 'Dorothy', 'Kevin', 'Carol', 'Brian', 'Amanda', 'George', 'Melissa',
    'Edward', 'Deborah', 'Ronald', 'Stephanie', 'Timothy', 'Rebecca', 'Jason', 'Sharon',
    'Jeffrey', 'Laura', 'Ryan', 'Cynthia', 'Jacob', 'Kathleen', 'Gary', 'Amy',
    'Nicholas', 'Shirley', 'Eric', 'Angela', 'Jonathan', 'Helen', 'Stephen', 'Anna',
    'Larry', 'Brenda', 'Justin', 'Pamela', 'Scott', 'Nicole', 'Brandon', 'Emma',
    'Benjamin', 'Samantha', 'Samuel', 'Katherine', 'Raymond', 'Christine', 'Patrick', 'Debra',
    'Alexander', 'Rachel', 'Jack', 'Catherine', 'Dennis', 'Carolyn', 'Jerry', 'Janet',
  ];
  return names[Math.floor(Math.random() * names.length)];
};

const getRandomLastName = () => {
  // prettier-ignore
  const names = [
    'Smith', 'Johnson', 'Williams', 'Brown', 'Jones', 'Garcia', 'Miller', 'Davis',
    'Rodriguez', 'Martinez', 'Hernandez', 'Lopez', 'Gonzalez', 'Wilson', 'Anderson', 'Thomas',
    'Taylor', 'Moore', 'Jackson', 'Martin', 'Lee', 'Perez', 'Thompson', 'White',
    'Harris', 'Sanchez', 'Clark', 'Ramirez', 'Lewis', 'Robinson', 'Walker', 'Young',
    'Allen', 'King', 'Wright', 'Scott', 'Torres', 'Nguyen', 'Hill', 'Flores',
    'Green', 'Adams', 'Nelson', 'Baker', 'Hall', 'Rivera', 'Campbell', 'Mitchell',
    'Carter', 'Roberts', 'Gomez', 'Phillips', 'Evans', 'Turner', 'Diaz', 'Parker',
    'Cruz', 'Edwards', 'Collins', 'Reyes', 'Stewart', 'Morris', 'Morales', 'Murphy',
    'Cook', 'Rogers', 'Gutierrez', 'Ortiz', 'Morgan', 'Cooper', 'Peterson', 'Bailey',
    'Reed', 'Kelly', 'Howard', 'Ramos', 'Kim', 'Cox', 'Ward', 'Richardson',
    'Watson', 'Brooks', 'Chavez', 'Wood', 'James', 'Bennett', 'Gray', 'Mendoza',
    'Ruiz', 'Hughes', 'Price', 'Alvarez', 'Castillo', 'Sanders', 'Patel', 'Myers',
    'Long', 'Ross', 'Foster', 'Jimenez',
  ];
  return names[Math.floor(Math.random() * names.length)];
};

/**
 * Returns a random net pay.
 * @returns A number between 1000 and 9900 (inclusive), rounded to hundreds.
 */
const getRandomNetPay = () => {
  return (Math.floor(Math.random() * 90) + 10) * 100;
};
